/**
 * Incident Investigation types (Module 24).
 *
 * Note: `erasableSyntaxOnly` is enabled in `tsconfig.app.json`, so we avoid `enum`
 * (enums emit runtime code). Use `as const` + union types instead.
 */

/**
 * Incident severity values (matches backend numeric enum).
 */
export const IncidentSeverities = {
  Low: 0,
  Medium: 1,
  High: 2,
  Critical: 3,
} as const;

export type IncidentSeverity = (typeof IncidentSeverities)[keyof typeof IncidentSeverities];

/**
 * Investigation status values (matches backend numeric enum).
 */
export const InvestigationStatuses = {
  NotStarted: 0,
  InProgress: 1,
  Paused: 2,
  Completed: 3,
  Cancelled: 4,
} as const;

export type InvestigationStatus = (typeof InvestigationStatuses)[keyof typeof InvestigationStatuses];

/**
 * Investigation step type values (matches backend numeric enum).
 */
export const InvestigationStepTypes = {
  DataCollection: 0,
  Analysis: 1,
  Hypothesis: 2,
  Verification: 3,
  Conclusion: 4,
} as const;

export type InvestigationStepType = (typeof InvestigationStepTypes)[keyof typeof InvestigationStepTypes];

/**
 * Investigation step status values (matches backend numeric enum).
 */
export const InvestigationStepStatuses = {
  Pending: 0,
  InProgress: 1,
  Completed: 2,
  Skipped: 3,
  Failed: 4,
} as const;

export type InvestigationStepStatus = (typeof InvestigationStepStatuses)[keyof typeof InvestigationStepStatuses];

/**
 * Message sender types (matches backend numeric enum).
 */
export const MessageSenderTypes = {
  Agent: 0,
  User: 1,
} as const;

export type MessageSenderType = (typeof MessageSenderTypes)[keyof typeof MessageSenderTypes];

/**
 * Incident investigation session DTO.
 */
export interface IncidentInvestigationSessionDto {
  id: string;
  agentDefinitionId: string;
  agentName: string;
  title: string;
  description?: string;
  severity: IncidentSeverity;
  investigationStatus: InvestigationStatus;
  relatedIncidentId?: string;
  incidentNumber?: string;
  incidentOccurredAt?: string;
  investigationStartedAt?: string;
  investigationCompletedAt?: string;
  rootCauseSummary?: string;
  remediationSteps?: string;
  preventionRecommendations?: string;
  messageCount: number;
  stepCount: number;
  correlationCount: number;
  createdAt: string;
  lastActivityAt?: string;
  tenantId: string;
  projectId?: string;
}

/**
 * Incident investigation step DTO.
 */
export interface IncidentInvestigationStepDto {
  id: string;
  investigationSessionId: string;
  stepName: string;
  description?: string;
  stepType: InvestigationStepType;
  status: InvestigationStepStatus;
  order: number;
  findingsJson?: string;
  toolsUsedJson?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
}

/**
 * Incident investigation message DTO.
 */
export interface IncidentInvestigationMessageDto {
  id: string;
  investigationSessionId: string;
  senderType: MessageSenderType;
  content: string;
  analysisType?: string;
  sequenceNumber: number;
  createdAt: string;
}

/**
 * Incident correlation DTO.
 */
export interface IncidentCorrelationDto {
  id: string;
  investigationSessionId: string;
  correlationType: string;
  relatedEntityId?: string;
  relatedEntityType?: string;
  correlationReason?: string;
  confidenceScore: number;
  createdAt: string;
}

/**
 * Investigation template DTO.
 */
export interface InvestigationTemplateDto {
  id: string;
  name: string;
  description?: string;
  stepsJson?: string;
  defaultPromptsJson?: string;
  isActive: boolean;
  isSystemTemplate: boolean;
  createdAt: string;
  updatedAt: string;
  tenantId: string;
  projectId?: string;
}

/**
 * Create investigation request.
 */
export interface CreateInvestigationRequest {
  agentDefinitionId: string;
  title: string;
  description?: string;
  severity: IncidentSeverity;
  relatedIncidentId?: string;
  incidentNumber?: string;
  incidentOccurredAt?: string;
  templateId?: string;
}

/**
 * Update investigation request.
 */
export interface UpdateInvestigationRequest {
  title?: string;
  description?: string;
  severity?: IncidentSeverity;
  investigationStatus?: InvestigationStatus;
  rootCauseSummary?: string;
  remediationSteps?: string;
  preventionRecommendations?: string;
}

/**
 * Complete investigation request.
 */
export interface CompleteInvestigationRequest {
  rootCauseSummary?: string;
  remediationSteps?: string;
  preventionRecommendations?: string;
}

/**
 * Add investigation step request.
 */
export interface AddInvestigationStepRequest {
  stepName: string;
  description?: string;
  stepType: InvestigationStepType;
  order: number;
}

/**
 * Update investigation step request.
 */
export interface UpdateInvestigationStepRequest {
  stepName?: string;
  description?: string;
  status?: InvestigationStepStatus;
  findingsJson?: string;
  toolsUsedJson?: string;
}

/**
 * Add correlation request.
 */
export interface AddCorrelationRequest {
  correlationType: string;
  relatedEntityId?: string;
  relatedEntityType?: string;
  correlationReason?: string;
  confidenceScore?: number;
}

/**
 * Investigation filters.
 */
export interface InvestigationFilters {
  pageNumber?: number;
  pageSize?: number;
  investigationStatus?: InvestigationStatus;
  severity?: IncidentSeverity;
  relatedIncidentId?: string;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Create investigation template request.
 */
export interface CreateInvestigationTemplateRequest {
  name: string;
  description?: string;
  stepsJson?: string;
  defaultPromptsJson?: string;
  isActive: boolean;
}

/**
 * Update investigation template request.
 */
export interface UpdateInvestigationTemplateRequest {
  name?: string;
  description?: string;
  stepsJson?: string;
  defaultPromptsJson?: string;
  isActive?: boolean;
}

